import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { ContextMenuContent, ContextMenuSubContentBase } from './ContextMenuContent.js';
import { ContextMenuSubContentContentMobile, ContextMenuContentMobile } from './ContextMenuContentMobile.js';
import { ContextMenuSubTrigger, ContextMenuItem } from './ContextMenuItem.js';
import { ContextMenuSeparator } from './ContextMenuSeparator.js';
import { cssVar } from '../../theme/variables.js';
import { preventForwardPropsConfig } from '../../utils/preventForwardProps.js';
import { styled, css } from 'styled-components';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import * as RadixContextMenu from '@radix-ui/react-context-menu';
import { useResponsive } from '../../hooks/useResponsive.js';
import { mediaNotMobile, mediaMobile } from '../../constants/styled-utils.js';
import { noop } from '@fabric-app/utils/guards/noop';
import React__default, { useContext, useState, createContext } from 'react';
import { ChevronRightIcon } from '@fabric-app/icons/icons';
import { DropdownItemIconContainer } from '../DropdownMenu/Item.js';
import { FabricHaptics } from '../../haptics/FabricHaptics.js';

const Overlay = styled.div.withConfig({
  displayName: "ContextMenu__Overlay",
  componentId: "sc-188fdeec-0"
})([
  `display:block;position:fixed;inset:0;z-index:125;background-color:`,
  `;`
], cssVar["color-bg-overlay"]);
const ContextMenuDemoBase = ({ children, content, contextDisabled, previewAnchor }) => {
  const { isMobileView } = useResponsive();
  const overlayRef = React__default.useRef(null);
  return /* @__PURE__ */ jsxs(RadixContextMenu.Root, {
    modal: true,
    onOpenChange: (value) => {
      if (value) {
        FabricHaptics.impact({
          style: FabricHaptics.ImpactStyle.Medium
        });
      }
    },
    children: [
      /* @__PURE__ */ jsx(RadixContextMenu.Trigger, {
        disabled: contextDisabled,
        asChild: true,
        children
      }),
      /* @__PURE__ */ jsx(RadixContextMenu.Portal, {
        container: getDefaultRadixPortalContainer(),
        children: /* @__PURE__ */ jsxs(Fragment, {
          children: [
            isMobileView && /* @__PURE__ */ jsx(Overlay, {
              ref: overlayRef
            }),
            isMobileView ? /* @__PURE__ */ jsx(ContextMenuContentMobile, {
              previewAnchor: previewAnchor ? children : null,
              onOpenChange: () => {
                const event = new KeyboardEvent("keydown", {
                  key: "Escape"
                });
                document.dispatchEvent(event);
              },
              children: content
            }) : /* @__PURE__ */ jsx(ContextMenuContent, {
              children: content
            })
          ]
        })
      })
    ]
  });
};
const Group = styled(RadixContextMenu.Group).withConfig(preventForwardPropsConfig([
  "borderBottom"
])).withConfig({
  displayName: "ContextMenu__Group",
  componentId: "sc-188fdeec-1"
})([
  ``,
  `{&:not(:first-child){margin-top:2px;padding-top:2px;border-top:1px solid rgba(255,255,255,0.1);}`,
  `}`,
  `{&:not(:first-child){margin-top:1.25rem;}}`
], mediaNotMobile, (p) => p.borderBottom && css([
  `&:not(:last-child){margin-bottom:2px;padding-bottom:2px;border-bottom:1px solid rgba(255,255,255,0.1);}`
]), mediaMobile);
const ContextMenuSubContext = /* @__PURE__ */ createContext({
  onOpenChange: noop,
  open: false
});
const useContextMenuSub = () => useContext(ContextMenuSubContext);
const ContextMenuSub = ({ open: openProp, onOpenChange: onOpenChangeProp, defaultOpen, ...props }) => {
  const [_open, _setOpen] = useState(openProp ?? defaultOpen ?? false);
  const open = openProp ?? _open;
  const setOpen = onOpenChangeProp ? onOpenChangeProp : _setOpen;
  return /* @__PURE__ */ jsx(ContextMenuSubContext.Provider, {
    value: {
      open,
      onOpenChange: setOpen
    },
    children: /* @__PURE__ */ jsx(RadixContextMenu.Sub, {
      open,
      onOpenChange: setOpen,
      defaultOpen,
      ...props
    })
  });
};
const ContextMenu = Object.assign(ContextMenuDemoBase, {
  Content: ContextMenuContent,
  Item: ContextMenuItem,
  ItemRaw: RadixContextMenu.Item,
  Separator: ContextMenuSeparator,
  Group,
  Sub: ContextMenuSub,
  Arrow: RadixContextMenu.Arrow,
  SubTrigger: ContextMenuSubTrigger,
  IconContainer: DropdownItemIconContainer,
  SubContent: (props) => {
    const { isMobileView } = useResponsive();
    const { onOpenChange } = useContextMenuSub();
    return isMobileView ? /* @__PURE__ */ jsx(ContextMenuSubContentContentMobile, {
      onOpenChange,
      ...props
    }) : /* @__PURE__ */ jsx(ContextMenuSubContentBase, {
      sideOffset: 8,
      alignOffset: 8,
      ...props
    });
  },
  Portal: RadixContextMenu.Portal,
  ChevronRight: () => /* @__PURE__ */ jsx(ChevronRightIcon, {
    style: {
      height: 14,
      marginLeft: "auto",
      marginRight: -4
    }
  })
});

export { ContextMenu };
//# sourceMappingURL=ContextMenu.js.map
